
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAudioData } from './AudioContext';
import { saveCalibrationDefaults, loadCalibrationDefaults } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';
import Swal from 'sweetalert2';

const AudioCalibrationTool = () => {
  const navigate = useNavigate();
  const [micModelName, setMicModelName] = useState('');
  const [micSensitivity, setMicSensitivity] = useState('');
  const [audioInterfaceModel, setAudioInterfaceModel] = useState('');
  const [dutModelName, setDutModelName] = useState('');
  const [dutSplCriteria, setDutSplCriteria] = useState('');
  const [osdVolumeSetting, setOsdVolumeSetting] = useState('');
  const [playPinkNoise, setPlayPinkNoise] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const { setCalibrationData } = useAudioData();
  const { calibrationData } = useAudioData();
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);

  // Audio ref for pink noise
  const audioRef = useRef(null);
  const PINK_NOISE_URL = 'https://mura-ai-images-dev.s3.eu-west-1.amazonaws.com/Uploads/Template/Pink-Noise.mp3';

  // Get user ID from token
  const user = decodedToken();
  const userId = user?.id;

  // For enabling the button
  const isFormComplete = micModelName.trim() !== "" &&
                         micSensitivity.trim() !== "" &&
                         audioInterfaceModel.trim() !== "" &&
                         dutModelName.trim() !== "" &&
                         dutSplCriteria.trim() !== "" &&
                         osdVolumeSetting.trim() !== "";

  // ✅ FIXED: Check if redirected from final submit
useEffect(() => {
    const redirectedFromSubmit = sessionStorage.getItem('redirectedFromSubmit') === 'true';
    const navigatedForward = sessionStorage.getItem('navigatedForward') === 'true';
    const hasCalibrationData = !!sessionStorage.getItem('calibrationData');  

    if (redirectedFromSubmit) {
    
      sessionStorage.removeItem('calibrationData');
      sessionStorage.removeItem('navigatedForward');
      sessionStorage.removeItem('redirectedFromSubmit');

      setMicModelName('');
      setMicSensitivity('');
      setAudioInterfaceModel('');
      setDutModelName('');
      setDutSplCriteria('');
      setOsdVolumeSetting('');
      setHasLoadedData(false);

      console.log('🗑️ Calibration data cleared after submit redirect');
    } else if (!navigatedForward && !hasCalibrationData) {
      
      sessionStorage.removeItem('calibrationData');
      sessionStorage.removeItem('navigatedForward');
      sessionStorage.removeItem('redirectedFromSubmit');

      setMicModelName('');
      setMicSensitivity('');
      setAudioInterfaceModel('');
      setDutModelName('');
      setDutSplCriteria('');
      setOsdVolumeSetting('');
      setHasLoadedData(false);

      console.log('🗑️ Calibration data cleared on fresh mount');
    } else {

      try {
        let savedData;
        if (hasCalibrationData) {
          savedData = JSON.parse(sessionStorage.getItem('calibrationData'));
        } else {
          if (calibrationData) {  
            savedData = calibrationData;
            sessionStorage.setItem('calibrationData', JSON.stringify(savedData));
          }
        }

        if (savedData) {
          setMicModelName(savedData.micModelName || '');
          setMicSensitivity(savedData.micSensitivity || '');
          setAudioInterfaceModel(savedData.audioInterfaceModel || '');
          setDutModelName(savedData.dutModelName || '');
          setDutSplCriteria(savedData.dutSplCriteria || '');
          setOsdVolumeSetting(savedData.osdVolumeSetting || '');
          setHasLoadedData(true); 

          console.log('🔄 Calibration data restored on back navigation:', savedData);
        } else {
          console.warn('⚠️ Back nav detected but no data to restore—treating as fresh');
          sessionStorage.removeItem('calibrationData');
          setMicModelName('');
          setMicSensitivity('');
          setAudioInterfaceModel('');
          setDutModelName('');
          setDutSplCriteria('');
          setOsdVolumeSetting('');
          setHasLoadedData(false);
        }
      } catch (error) {
        console.error(' Failed to restore calibration data:', error);
        sessionStorage.removeItem('calibrationData');
        setMicModelName('');
        setMicSensitivity('');
        setAudioInterfaceModel('');
        setDutModelName('');
        setDutSplCriteria('');
        setOsdVolumeSetting('');
        setHasLoadedData(false);
      }
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);  

  // Save form data to session storage whenever it changes
  useEffect(() => {
    if (hasLoadedData) {
      const dataToSave = {
        micModelName,
        micSensitivity,
        audioInterfaceModel,
        dutModelName,
        dutSplCriteria,
        osdVolumeSetting
      };
      sessionStorage.setItem('calibrationData', JSON.stringify(dataToSave));
    }
  }, [micModelName, micSensitivity, audioInterfaceModel, dutModelName, dutSplCriteria, osdVolumeSetting, hasLoadedData]);

  // Handle pink noise playback
  useEffect(() => {
    if (audioRef.current) {
      if (playPinkNoise) {
        audioRef.current.play().catch(error => {
          console.error('Error playing pink noise:', error);
          Swal.fire({
            icon: 'error',
            title: 'Playback Error',
            text: 'Failed to play pink noise. Please check your audio settings.',
            confirmButtonColor: '#3b82f6',
          });
          setPlayPinkNoise(false);
        });
      } else {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [playPinkNoise]);

  const handleBackClick = () => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Clear session storage when navigating back
    sessionStorage.removeItem('calibrationData');
    sessionStorage.removeItem('navigatedForward');
    sessionStorage.removeItem('redirectedFromSubmit');
    console.log('🗑️ Calibration data cleared on back navigation');

    navigate('/dashboard');
  };
  const hasInputData =
  micModelName ||
  micSensitivity ||
  audioInterfaceModel ||
  dutModelName ||
  dutSplCriteria ||
  osdVolumeSetting;

  const handleSaveDefaults = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'warning',
        title: 'Authentication Error',
        text: 'User not authenticated. Please login again.',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }

    if (!micModelName || !micSensitivity || !audioInterfaceModel) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Form',
        text: 'Please fill in all MIC and Audio Interface information before saving.',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }

    setIsSaving(true);
    try {
      const calibrationData = {
        micModelName,
        micSensitivity,
        audioInterfaceModel
      };

      await saveCalibrationDefaults(userId, calibrationData);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Test Configuration defaults saved successfully!',
        confirmButtonColor: '#3b82f6',
      });
    } catch (error) {
      console.error('Save defaults error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Error',
        text: `Failed to save defaults: ${error.message}`,
        confirmButtonColor: '#3b82f6',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadDefaults = async () => {
  if (!userId) {
    Swal.fire({
      icon: 'warning',
      title: 'Authentication Error',
      text: 'User not authenticated. Please login again.',
      confirmButtonColor: '#3b82f6',
    });
    return;
  }

  setIsLoading(true);

  try {
    const data = await loadCalibrationDefaults(userId);

    const hasValidDefaults =
      data &&
      (
        data.mic_model ||
        data.mic_sensitivity ||
        data.audio_interface_model
      );

    if (hasValidDefaults) {
      setMicModelName(data.mic_model || '');
      setMicSensitivity(data.mic_sensitivity || '');
      setAudioInterfaceModel(data.audio_interface_model || '');

      setHasLoadedData(true);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Calibration defaults loaded successfully!',
        confirmButtonColor: '#3b82f6',
      });

      console.log('Calibration defaults loaded:', data);
    } else {
      Swal.fire({
        icon: 'info',
        title: 'No Defaults Found',
        text: 'No calibration defaults are saved for this account.',
        confirmButtonColor: '#3b82f6',
      });
    }

  } catch (error) {
    console.error('Load defaults error:', error);

    Swal.fire({
      icon: 'error',
      title: 'Load Error',
      text: 'Failed to load calibration defaults. Please try again.',
      confirmButtonColor: '#3b82f6',
    });

  } finally {
    setIsLoading(false);
  }
};

  const handlePlayPinkNoise = () => {
    setPlayPinkNoise(true);
  };

  const handleStopPinkNoise = () => {
    setPlayPinkNoise(false);
  };

  const DoneandNext = () => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Validate that at least MIC configuration is filled
    if (!micModelName || !micSensitivity || !audioInterfaceModel) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Configuration',
        text: 'Please fill in at least the MIC Information and Audio Interface before proceeding.',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }

    setCalibrationData({
      micModelName,
      micSensitivity,
      audioInterfaceModel,
      dutModelName,
      dutSplCriteria,
      osdVolumeSetting,
    });

    // Mark that user navigated forward (so data persists on return)
    sessionStorage.setItem('navigatedForward', 'true');
    
    navigate('/audio-recording');
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900 text-white">
      {/* Hidden audio element for pink noise */}
      <audio
        ref={audioRef}
        src={PINK_NOISE_URL}
        loop
        preload="auto"
        onError={(e) => {
          console.error('Audio loading error:', e);
          Swal.fire({
            icon: 'error',
            title: 'Audio Error',
            text: 'Failed to load pink noise audio.',
            confirmButtonColor: '#3b82f6',
          });
          setPlayPinkNoise(false);
        }}
      />

      <div className="fixed top-0 left-0 w-full flex items-center p-4 border-b border-gray-700 bg-black z-50">
        <button className="mr-3 text-white hover:text-white">
          <ArrowLeft className="w-6 h-6" onClick={handleBackClick} />
        </button>
        <h1 className="text-xl font-bold text-white">Audio Calibration Tool</h1>
      </div>

      <div className="flex flex-col md:flex-row flex-1 bg-black gap-10 pt-18 md:pt-20 pb-20 px-2 md:px-3 overflow-y-auto md:overflow-hidden">
        <div className="w-full md:w-1/2 px-4 md:px-5 py-4 md:overflow-y-auto bg-gray-800">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Test Configuration</h2>

          <div className="flex flex-col sm:flex-row gap-4 mb-5">
            <div className="flex-1">
              <label className="block text-sm text-gray-300 font-medium mb-2 min-h-[40px]">
                MIC Information - Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={micModelName}
                onChange={(e) => setMicModelName(e.target.value)}
                className="w-full p-2 bg-black border border-gray-600 rounded-md text-white placeholder-gray-500"
                placeholder="Enter Model"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm text-gray-300 font-medium mb-2 min-h-[40px]">
                MIC Information - Sensitivity <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={micSensitivity}
                onChange={(e) => setMicSensitivity(e.target.value)}
                className="w-full p-2 bg-black  border border-gray-600 rounded-md text-white placeholder-gray-500"
                placeholder="Enter Sensitivity"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm text-gray-300 font-medium mb-2">
              Audio Recording Interface - Model <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={audioInterfaceModel}
              onChange={(e) => setAudioInterfaceModel(e.target.value)}
              className="w-full p-2 bg-black  border border-gray-600 rounded-md text-white placeholder-gray-500"
              placeholder="Enter Model"
            />
          </div>

          <div className="flex gap-4 mb-7">
             <button
              onClick={handleLoadDefaults}
              disabled={isLoading}
              className="
              flex-1 px-4 py-2 
              bg-gray-700 border border-gray-600 rounded-md 
              hover:bg-gray-600 
              text-sm flex items-center justify-center gap-2 
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Loading...' : 'Load Defaults'}
          </button>
          <button
          onClick={handleSaveDefaults}
          disabled={isSaving || !hasInputData || defaultsLoaded}
          className="
            flex-1 px-4 py-2 
            bg-blue-600 border border-blue-500 rounded-md 
            hover:bg-blue-700 
            text-sm flex items-center justify-center gap-2 
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSaving ? 'Saving...' : 'Save Defaults'}
        </button>
          </div>
          <div className="border border-gray-700 mb-6" />

          <div className="flex flex-col sm:flex-row gap-4 mb-5 mt-3">
            <div className="flex-1">
              <label className="block text-sm text-gray-300 font-medium mb-2 min-h-[40px]">
                DUT Information - Model Name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={dutModelName}
                onChange={(e) => setDutModelName(e.target.value)}
                className="w-full p-2 bg-black border border-gray-600 rounded-md text-white placeholder-gray-500"
                placeholder="Enter Model Name"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm text-gray-300 font-medium mb-2 min-h-[40px]">
                DUT Information - SPL Criteria<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={dutSplCriteria}
                onChange={(e) => setDutSplCriteria(e.target.value)}
                className="w-full p-2 bg-black  border border-gray-600 rounded-md text-white placeholder-gray-500"
                placeholder="Enter SPL Criteria"
              />
            </div>
          </div>

          <div className="mb-7">
            <label className="block text-sm text-gray-300 font-medium mb-2">DUT Calibration<span className="text-red-500">*</span></label>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handlePlayPinkNoise}
                disabled={playPinkNoise}
                className={`flex-1 px-4 py-2 rounded-md border flex items-center justify-center gap-2 text-sm transition-colors ${
                  playPinkNoise
                    ? 'bg-gray-600 border-gray-500 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-white'
                }`}
              >
                ▶ {playPinkNoise ? 'Playing Pink Noise...' : 'Play Pink Noise'}
              </button>

              <button
                onClick={handleStopPinkNoise}
                disabled={!playPinkNoise}
                className={`flex-1 px-4 py-2 rounded-md border flex items-center justify-center gap-2 text-sm transition-colors ${
                  playPinkNoise
                    ? 'bg-red-600 border-red-500 hover:bg-red-700 text-white'
                    : 'bg-gray-600 border-gray-500 text-gray-400 cursor-not-allowed'
                }`}
              >
                ■ Stop Pink Noise
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 font-medium mb-2">OSD Volume Setting<span className="text-red-500">*</span></label>
            <input
              type="text"
              value={osdVolumeSetting}
              onChange={(e) => setOsdVolumeSetting(e.target.value)}
              className="w-full p-2 bg-black  border border-gray-600 rounded-md text-white placeholder-gray-500"
              placeholder="Enter Volume Setting"
            />
          </div>
        </div>

        <div className="w-full md:w-1/2 p-4 md:p-6 md:overflow-y-auto bg-gray-800">
          <h2 className="text-xl md:text-2xl font-bold mb-5">Operation Instructions</h2>

          <ol className="list-decimal text-gray-300 list-inside space-y-2 mb-9 text-sm">
            <li>Connect the microphone to the audio interface.</li>
            <li>Place the microphone at the designated test position.</li>
            <li>Ensure the DUT (Device Under Test) is properly set up.</li>
            <li>Start the pink noise playback for calibration.</li>
            <li>Adjust the OSD volume setting as needed.</li>
            <li>Verify the SPLA 1m dB value against the reference table below.</li>
          </ol>

          <h3 className="text-lg md:text-xl font-semibold mb-5">Calibration Reference Table</h3>

          <div className="overflow">
            <table className="w-full border-b border-gray-600 text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-800">
                  <th className="bg-black border-b border-gray-600 p-2 sm:p-3 text-left w-1/2 whitespace-nowrap">
                    TV Panel Size
                  </th>
                  <th className="bg-black border-b border-gray-600 p-2 sm:p-3 text-left whitespace-nowrap pl-2 sm:pl-3">
                    SPLA 1m dB
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 whitespace-nowrap">
                    15&quot; ≤ TV Panel Size ≤ 32&quot;
                  </td>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 pl-8 sm:pl-10 whitespace-nowrap">
                    66dB
                  </td>
                </tr>
                <tr>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 whitespace-nowrap">
                    32&quot; &lt; TV Panel Size ≤ 50&quot;
                  </td>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 pl-8 sm:pl-10 whitespace-nowrap">
                    67dB
                  </td>
                </tr>
                <tr>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 whitespace-nowrap">
                    50&quot; &lt; TV Panel Size
                  </td>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 pl-8 sm:pl-10 whitespace-nowrap">
                    70dB
                  </td>
                </tr>
                <tr>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 whitespace-nowrap">
                    PHILIPS OLED, 9000 series above
                  </td>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 pl-8 sm:pl-10 whitespace-nowrap">
                    75dB
                  </td>
                </tr>
                <tr>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 whitespace-nowrap">
                    PHILIPS B&amp;W model
                  </td>
                  <td className="text-gray-300 border-b border-gray-600 p-2 sm:p-3 pl-8 sm:pl-10 whitespace-nowrap">
                    78dB
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="p-4 bg-black border-t border-gray-700 fixed bottom-0 left-0 right-0 flex justify-end">
        <button
          onClick={DoneandNext}
          disabled={!isFormComplete}
          className={`px-6 py-2 rounded-md border ${
            isFormComplete
              ? "bg-blue-600 border-blue-500 hover:bg-blue-700 text-white"
              : "bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed"
          }`}
        >
          Done & Next
        </button>
      </div>
    </div>
  );
};

export default AudioCalibrationTool;

